import React, { useContext } from "react";
import { isMobile, isIOS } from "react-device-detect";
import styles from "./ShareArrow.module.scss";
import { CopyToClipboard } from "react-copy-to-clipboard";
import CopiedContext from "../CopiedContext";
import { trackEvent } from "../utils/helpers";

const ShareArrow = ({
  title,
  url,
  text,
  downloadUrl,
  isVideo = false,
  customBottom = "8px",
  sectionName,
  mediaShare,
  cityurl,
  venue,
  azureurl,
  videourl,
  guid,
  country,
}) => {
  const { showCopied, setShowCopied } = useContext(CopiedContext);

  // Build mediaShareURL like ShareBar does
  const mediaShareAddress = `https://${cityurl}/stories-shared-media`;
  let mediaShareURL = "";
  let shareText = "";

  const thumbnailParts = mediaShare?.thumbnail?.split("/");
  const filename = thumbnailParts?.[4];
  const thumbnailURL = filename?.split("_")[0];

  // Extract venue parameter for all URLs
  const venueParam = venue ? `&venue=${encodeURIComponent(venue)}` : "";

  // Use the text prop passed from the component, which already has the correct format
  shareText = text ? `${text} ` : "Check out this content from our Flight Club visit. ";

  if (mediaShare?.mediaType === "PHOTO") {
    mediaShareURL = `${mediaShareAddress}?u=photo/${mediaShare.mediaURI.substring(0, mediaShare.mediaURI.lastIndexOf("."))}&t=i&section=group_photo${venueParam}`;
  } else if (mediaShare?.mediaType === "VIDEO" && sectionName === "Action Replay") {
    if (mediaShare?.videoURL) {
      const azureCode = mediaShare.videoURL
        .replace(azureurl, "")
        .replace(videourl, "")
        .replace(".ism/manifest", "");
      const vidType = mediaShare.videoURL.includes(".mp4") ? "vidblob" : "vidaz";
      const eventType = mediaShare?.videoType ? `&event=${mediaShare.videoType}` : "";
      mediaShareURL = `${mediaShareAddress}?u=${vidType}${azureCode}&t=v&p=a&poster=${thumbnailURL}&section=action_replay${eventType}${venueParam}`;
    }
  } else if (mediaShare?.mediaType === "VIDEO" && sectionName === "Loop Cams") {
    if (mediaShare?.videoURL) {
      const azureCode = mediaShare.videoURL
        .replace(azureurl, "")
        .replace(videourl, "")
        .replace(".ism/manifest", "");
      const vidType = mediaShare.videoURL.includes(".mp4") ? "vidblob" : "vidaz";
      mediaShareURL = `${mediaShareAddress}?u=${vidType}${azureCode}&t=v&p=a&poster=${thumbnailURL}&section=loop_cam${venueParam}`;
    }
  }

  if (country === "us") {
    mediaShareURL = `https://stories.us.flightclubdarts.com/${guid}`;
  }

  const handleShare = async (event) => {
    // Prevent the click from bubbling up to parent elements
    event.stopPropagation();

    trackEvent("Share", "Native Share " + sectionName, "Mobile Share Button");

    // Create the native share text with current story URL
    const currentUrl = window.location.href;
    const fullShareText = `${shareText}See the full Story here: ${currentUrl}?utm_source=Stories&utm_medium=native_share&utm_campaign=native`;

    // Check if native share is supported
    if (!navigator.share) {
      // Fallback to copy URL
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(fullShareText);
          setShowCopied(true);
        } catch (error) {
          // console.log("Failed to copy URL:", error);
        }
      }
      return;
    }

    // iOS: Try to fetch and share the actual file (full functionality)
    if (isIOS && downloadUrl) {
      try {
        const response = await fetch(downloadUrl);
        const blob = await response.blob();
        const fileName = isVideo ? "flight-club-video.mp4" : "flight-club-photo.jpg";
        const file = new File([blob], fileName, { type: blob.type });

        // Check if we can share files
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: title || "Flight Club Stories",
            text: fullShareText,
            files: [file],
          });
          return;
        }
      } catch (error) {
        // If the share dialog was shown but cancelled/failed, don't fall through to a second share
        if (error.name === "AbortError" || error.name === "NotAllowedError") return;
        // console.log("Error sharing file on iOS:", error);
      }
    }

    // Fallback: Share URL and text (works on both iOS and Android)
    try {
      const shareData = {
        title: title || "Flight Club Stories",
        text: fullShareText,
      };

      // Add URL separately for iOS (better support)
      if (isIOS) {
        shareData.url = currentUrl;
      }

      await navigator.share(shareData);
    } catch (error) {
      // console.log("Error sharing:", error);
      // Final fallback to copy URL if share fails
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(fullShareText);
          // console.log("Share failed, URL copied to clipboard instead");
          setShowCopied(true);
        } catch (clipboardError) {
          // console.log("Failed to copy URL:", clipboardError);
        }
      }
    }
  };

  const handleLinkCopy = () => {
    trackEvent("Share", "Share " + sectionName, "Link");
    setShowCopied(true);
  };

  return (
    <div
      className={styles.shareContainer}
      style={{ bottom: customBottom }}
      onMouseEnter={(e) => e.stopPropagation()}
      onMouseLeave={(e) => e.stopPropagation()}
    >
      {/* Download Button - desktop only */}
      {!isMobile && (
        <a
          href={downloadUrl}
          download
          className={`${styles.shareIcon} ${sectionName === "Group Photo" || sectionName === "Loop Cams" ? styles.groupPhotoShare : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            trackEvent("Share", "Download " + sectionName, "Download Button");
          }}
          aria-label="Download"
        >
          <span>Save</span>
          {(() => {
            const saveIconColor =
              sectionName === "Group Photo" || sectionName === "Loop Cams" ? "#D8B464" : "#B59754";
            const SaveSvg = () => (
              <svg
                width="15"
                height="16"
                viewBox="0 0 15 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M13.5 14C14.0523 14 14.5 14.4477 14.5 15C14.5 15.5523 14.0523 16 13.5 16H1C0.447715 16 0 15.5523 0 15C0 14.4477 0.447715 14 1 14H13.5Z"
                  fill={saveIconColor}
                />
                <path d="M7.5 4.85454L13.125 2.5L7.5 12.5L1.875 2.5L7.5 4.85454Z" fill={saveIconColor} />
              </svg>
            );
            return (
              <div className={styles.saveIconTrack}>
                <div className={styles.saveIconExit}>
                  <SaveSvg />
                </div>
                <div className={styles.saveIconEnter}>
                  <SaveSvg />
                </div>
              </div>
            );
          })()}
        </a>
      )}

      {/* Share Button - Only show on mobile */}
      {isMobile && (
        <button
          className={`${styles.shareIcon} ${sectionName === "Group Photo" || sectionName === "Loop Cams" ? styles.groupPhotoShare : ""}`}
          onClick={handleShare}
          aria-label="Share"
        >
          <span>Share</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M7.15794 16.6648L10.1184 10.0122L19.4495 10.1332L7.15794 16.6648ZM10.1083 9.22757L7.34104 2.54903L19.3736 9.34776L10.1083 9.22757Z"
              fill="#B59754"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default ShareArrow;
